  import React from "react";
import {
  Drawer,
  Avatar,
  Typography,
  Tag,
  Space,
  Collapse,
  Divider,
  Row,
  Col,
  Button,
  message,
  Spin,
  Tooltip,
} from "antd";
import {
  MailOutlined,
  PhoneOutlined,
  HomeOutlined,
  UserOutlined,
  ArrowUpOutlined,
  FilePdfOutlined,
  CheckCircleFilled,
  EnvironmentOutlined,
  Loading3QuartersOutlined,
  CopyOutlined,
} from "@ant-design/icons";
import RedirectIcon from "../../Assets/advert_detail/redirect.svg";
import CheckMark from "../../Assets/profile/checkMark.svg";
import { UpdatestatusPopup } from "../../components/common/AdvertDetail/Reportpopup";
import { jobStatusEnum, openDocumentInNewTab } from "../../utils";
import API_MANAGER from "../../API";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import JobPositionCard from "../../components/common/campaignManagement/JobPositionCard";
import JobPositionCardGrouped, {
  groupExperiences,
} from "../../components/common/campaignManagement/JobPositionCardGrouped";
import { USE_GROUPED_JOB_EXPERIENCE } from "../../components/common/campaignManagement/jobExperienceUi";

const { Title, Text, Paragraph, Link } = Typography;
const { Panel } = Collapse;

const ProfileDrawer = ({ 
  open, 
  setOpen, 
  data, 
  advertId,
  currentPage,
  search,
  filterType,
  tabType,
  locationState
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);
  const [candidateData, setCandidateData] = React.useState(null);
  const [updatePopup, setUpdatePopup] = React.useState(false);
  const [candidateSelectedData, setCandidateSelectedData] = React.useState({});
  const [candidateProgress, setCandidateProgress] = React.useState("");
  const [candidateId, setCandidateId] = React.useState(null);
  const [isFavorite, setIsFavorite] = React.useState(false);
  const [favoriteLoading, setFavoriteLoading] = React.useState(false);
  const [isHidden, setIsHidden] = React.useState(true);
  const [drawerWidth, setDrawerWidth] = React.useState(546);
  const [windowWidth, setWindowWidth] = React.useState(window.innerWidth);
  const userType = localStorage.getItem("user");

  React.useEffect(() => {
    const updateDrawerWidth = () => {
      const screenWidth = window.innerWidth;
      setWindowWidth(screenWidth);
      if (screenWidth >= 2560) {
        setDrawerWidth(1400);
      } else if (screenWidth >= 1920) {
        setDrawerWidth(1200);
      } else {
        setDrawerWidth(546);
      }
    };

    updateDrawerWidth();
    window.addEventListener('resize', updateDrawerWidth);
    return () => window.removeEventListener('resize', updateDrawerWidth);
  }, []);

  const replaceString = (string) => {
    if (typeof string === "number") {
      string = string?.toString();
    }
    return string?.replace(/[^\s]/g, "x");
  };

  const handleCopy = (text) => {
    if (!text || text === "----") return;
    navigator.clipboard
      .writeText(text)
      .then(() => message.success("Copied to clipboard."))
      .catch(() => message.error("Failed to copy."));
  };

  const getCandidateDetails = async (id) => {
    setLoading(true);
    try {
      const response = await API_MANAGER.getAppliedCandidateDetails(id);
      console.log(response?.data?.data);
      if (response?.data?.data) {
        setCandidateData(response.data.data);
        setCandidateId(id);
        
        // Check favorite status after candidate data is loaded
        if (response.data.data?.candidate?._id) {
          await checkFavoriteStatus(response.data.data.candidate._id);
        }
      } else {
        message.error("Failed to load candidate details.");
      }
    } catch (error) {
      console.error("Error fetching candidate details:", error);
      message.error("Something went wrong. Please try again.");
      setCandidateData(null);
    } finally {
      setLoading(false);
    }
  };

  // Check if candidate is in favorites
  const checkFavoriteStatus = async (candidateId) => {
    try {
      const response = await API_MANAGER.getAllFavourites();
      const favorites = response?.data?.data || [];
      
      // Check if current candidate is in the favorites list
      const isInFavorites = favorites.some(fav => 
        fav.candidateId?._id === candidateId || 
        fav.candidateId?.id === candidateId
      );
      
      setIsFavorite(isInFavorites);
    } catch (error) {
      console.error("Error checking favorite status:", error);
    }
  };

  const toggleFavorite = async () => {
    setFavoriteLoading(true);
    try {
      if (isFavorite) {
        // Remove from favorites
        await API_MANAGER.removeFavourite(candidateData?.candidate?._id);
        setIsFavorite(false);
      } else {
        // Add to favorites
        await API_MANAGER.addFavourite({ candidateId: candidateData?.candidate?._id });
        setIsFavorite(true);
      }
    } catch (error) {
      message.error("Failed to update favorite status.");
    } finally {
      setFavoriteLoading(false);
    }
  };

  React.useEffect(() => {
    if (open && data?.profileId?.userId?._id != null) {
      getCandidateDetails(data.profileId.userId._id);
    }
  }, [open, data?.profileId?.userId?._id]);

  React.useEffect(() => {
    if (candidateData && candidateData?.candidate && advertId) {
      const appliedJobs = candidateData?.candidate?.appliedJobs;
      const appliedJobData = Array.isArray(appliedJobs)
        ? appliedJobs.find(
            (jobs) => {
              const jobAdvertId = jobs?.advertId?.id || jobs?.advertId?._id;
              return jobAdvertId === advertId || jobAdvertId === String(advertId);
            }
          )
        : null;

      setCandidateSelectedData(appliedJobData || { isInvited: true });
      
      // Find the highest state in the progress array (not just the last one)
      let applicationState = -1;
      if (appliedJobData && appliedJobData.progress && Array.isArray(appliedJobData.progress) && appliedJobData.progress.length > 0) {
        // Get all states from progress array and filter valid ones
        const states = appliedJobData.progress
          .map(p => p?.state)
          .filter(state => state !== null && state !== undefined && state !== -1 && typeof state === 'number');
        
        if (states.length > 0) {
          // Find the highest state (0=APPLIED, 1=ACCEPTED, 2=SHORTLIST_OFFERED, 3=HARD_REVIEW, 4=ROLE_OFFERED, 5=ROLE_ACCEPTED)
          applicationState = Math.max(...states);
        }
      }
      
      const isViewable = locationState?.isViewable;
      if (applicationState > 2 || isViewable) {
        setIsHidden(false);
      } else {
        setIsHidden(true);
      }
      
      // Map state to status label using jobStatusEnum
      if (applicationState !== -1 && applicationState !== undefined && applicationState >= 0 && applicationState <= 5) {
        const applicantStatus = jobStatusEnum[applicationState];
        setCandidateProgress(applicantStatus?.label || "INVITED");
      } else {
        setCandidateProgress("INVITED");
      }
    } else if (!advertId && candidateData) {
      // If no advertId provided, show INVITED
      setCandidateProgress("INVITED");
      setCandidateSelectedData({ isInvited: true });
    }
  }, [candidateData, advertId, locationState]);

  const updateCandidateJobStatus = async () => {
    try {
      const currentState = candidateSelectedData?.progress?.[
        candidateSelectedData?.progress?.length - 1
      ]?.state;
      
      // Recruiter actions: Can offer Shortlist from state 0, and offer Role from state 3
      if (currentState === 0 || currentState === "0" || Number(currentState) === 0) {
        // Recruiter can offer Shortlist from Applied state
        await API_MANAGER.updateCandidateJobStatus(candidateSelectedData?._id, {
          status: "SHORTLIST_OFFERED",
        });
      } else if (
        candidateSelectedData?.progress[
          candidateSelectedData?.progress?.length - 1
        ]?.state === 2
      ) {
        // Candidate action: Accept Shortlist Offer (state 2 → 3)
        // This should be done by candidate, but recruiter can trigger it
        await API_MANAGER.acceptShortlistOffer({
          applicationId: candidateSelectedData?._id,
          acceptance: true,
        });
      } else if (
        candidateSelectedData?.progress[
          candidateSelectedData?.progress?.length - 1
        ]?.state === 3
      ) {
        // Recruiter action: Offer Role from Hard Review state
        await API_MANAGER.updateCandidateJobStatus(candidateSelectedData?._id, {
          status: "ROLE_OFFERED",
        });
      } else {
        // Invalid state transition
        setUpdatePopup(false);
        return;
      }

      message.success("Status successfully updated.");
      setUpdatePopup(false);
      if (candidateId) {
        getCandidateDetails(candidateId);
      }
    } catch (error) {
      message.error(
        error?.response?.data?.message ||
          "A Candidate must accept your previous request, before you can push them to the next stage of the recruitment process."
      );
      setUpdatePopup(false);
    }
  };

  return (
    <Drawer
      title={null}
      onClose={() => setOpen(false)}
      closeIcon={null}
      open={open}
      width={drawerWidth}
      className="profile-drawer"
      contentWrapperStyle={{
        margin: " 8px 16px",
      }}
      bodyStyle={{
        background: "#fff",
        borderRadius: "16px",
        padding: "20px",
      }}
    >
      <Spin spinning={loading}>
        <div className="header">
          <div className="avatar-row">
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "8px" }}>
              <Avatar
                size={122}
                src={candidateData?.candidate?.profileImage ?? <UserOutlined />}
                className={isHidden ? "avatar text_blur" : "avatar"}
              />
              <span
                className={`tag ${userType !== "Associate" ? "cursor-pointer" : ""}`}
                onClick={() => {
                  userType !== "Associate" && setUpdatePopup(true);
                }}
                style={{
                  cursor: userType !== "Associate" ? "pointer" : "default",
                  padding: windowWidth <= 768 ? "4px 8px" : undefined,
                  fontSize: windowWidth <= 768 ? "11px" : undefined,
                  whiteSpace: "nowrap",
                  maxWidth: windowWidth <= 768 ? "100px" : "none",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  marginTop: "12px",
                }}
              >
                {candidateProgress || "INVITED"}
              </span>
            </div>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <p className="client-name" style={{ textTransform: "capitalize" }}>{candidateData?.candidate?.name}</p>
                {userType !== "Associate" && (
                <Tooltip title={isFavorite ? "Remove from favourite" : "Click to mark as favourite"} placement="top">
                  <div
                    onClick={toggleFavorite}
                    style={{
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "24px",
                      height: "24px",
                      borderRadius: "50%",
                      backgroundColor: "white",
                      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                      transition: "all 0.2s ease",
                      flexShrink: 0,
                      marginBottom:"20px",
                    }}
                  >
                    {isFavorite ? (
                      <div
                        style={{
                          width: "18px",
                          height: "18px",
                          borderRadius: "50%",
                          backgroundColor: "#facc15",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          
                        }}
                      >
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
                            fill="white"
                          />
                        </svg>
                      </div>
                    ) : (
                      <div
                        style={{
                          width: "18px",
                          height: "18px",
                          borderRadius: "50%",
                          border: "1.5px solid #666",
                          backgroundColor: "white",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
                            stroke="#666"
                            strokeWidth="1.5"
                            fill="none"
                          />
                        </svg>
                      </div>
                    )}
                  </div>
                </Tooltip>
                )}
              </div>
                <div style={{ marginTop: "0px", textAlign: "left", marginBottom: "14px" }}>
                {candidateData?.candidate?.documents?.some(
                    (doc) => doc?.documentType?.name === "VISA"
                  ) ? (
                      <Tag 
                        color="orange" 
                        style={{ 
                          fontSize: "14px", 
                          padding: "4px 12px",
                          borderRadius: "6px",
                          fontWeight: 700,
                      margin: 0,
                      color: "#d46b08"
                        }}
                      >
                        Non-Australian
                      </Tag>
                    ) : (
                      <Tag 
                    color="orange" 
                        style={{ 
                          fontSize: "14px", 
                          padding: "4px 12px",
                          borderRadius: "6px",
                          fontWeight: 700,
                      margin: 0,
                      color: "#d46b08"
                        }}
                      >
                        Australian/New Zealand
                      </Tag>
                  )}
                </div>
              {/* Address - moved above CV */}
              {candidateData?.candidate?.street && (
                <p style={{ marginTop: "8px", marginBottom: 0 }}>
                  <Space align="start">
                    <EnvironmentOutlined style={{ marginTop: "2px", fontSize: "14px" }}/>
                    {!isHidden ? (
                      <Text type="primary" style={{ fontSize: 14, color: "#071135" }}>
                        {candidateData?.candidate?.street?.charAt(0)?.toUpperCase() +
                          candidateData?.candidate?.street?.substring(1) +
                          ", " +
                          candidateData?.candidate?.city +
                          ", " +
                          candidateData?.candidate?.state +
                          ", " +
                          candidateData?.candidate?.postalCode +
                          ", " +
                          candidateData?.candidate?.country || ""}
                      </Text>
                    ) : (
                      <>
                        <Text type="primary" style={{ fontSize: 14, color: "#071135", filter: "blur(4px)" }}>
                          {replaceString(candidateData?.candidate?.street)}
                        </Text>
                        <Text type="primary" style={{ fontSize: 14, color: "#071135" }}>
                          {[
                            candidateData?.candidate?.city,
                            candidateData?.candidate?.state,
                            candidateData?.candidate?.postalCode,
                            candidateData?.candidate?.country
                          ].filter(Boolean).join(", ")}
                        </Text>
                      </>
                    )}
                  </Space>
                </p>
              )}
              {candidateData?.candidate?.resume?.[0]?.resume && !isHidden && (
                <div
                  onClick={() => {
                    openDocumentInNewTab(
                      candidateData?.candidate?.resume?.[0]?.resume
                    );
                  }}
                  style={{
                    marginTop: "12px",
                    padding: "12px 16px",
                    background: "#F8F9FF",
                    border: "1px solid #E4E8ED",
                    borderRadius: "12px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    transition: "all 0.2s ease",
                    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "#F0F2FF";
                    e.currentTarget.style.borderColor = "#D1D5DB";
                    e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.1)";
                    e.currentTarget.style.transform = "translateY(-1px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "#F8F9FF";
                    e.currentTarget.style.borderColor = "#E4E8ED";
                    e.currentTarget.style.boxShadow = "0 1px 3px rgba(0, 0, 0, 0.05)";
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                >
                  <div
                    style={{
                      width: "40px",
                      height: "40px",
                      background: "#FFF",
                      borderRadius: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "1px solid #E4E8ED",
                      flexShrink: 0
                    }}
                  >
                    <FilePdfOutlined style={{ fontSize: "20px", color: "#DC2626" }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Text
                      style={{
                        fontSize: "14px",
                        fontWeight: 600,
                        color: "#071135",
                        display: "block",
                        marginBottom: "2px"
                      }}
                    >
                      Curriculum Vitae
                    </Text>
                    <Text
                      style={{
                        fontSize: "12px",
                        color: "#6B7280",
                        display: "block"
                      }}
                    >
                      Click to download PDF
                    </Text>
                  </div>
                  <ArrowUpOutlined
                    style={{
                      fontSize: "16px",
                      color: "#6B7280",
                      transform: "rotate(45deg)",
                      flexShrink: 0
                    }}
                  />
                </div>
              )}
            </div>
          </div>
          <div>
            <p
              onClick={() =>
                navigate(`/applicant-profile/${data?.profileId?.userId?._id}`, {
                  state: {
                    id: advertId,
                    ...(tabType ? { fromTracker: true } : {}),
                    currentPage: currentPage,
                    search: search !== null && search !== undefined ? search : undefined,
                    tabType: tabType || undefined,
                    filterType: filterType !== undefined ? filterType : undefined,
                    campaignId: locationState?.campaignId,
                    fromCampaignDetail: locationState?.fromCampaignDetail,
                    originalSource: locationState?.originalSource
                  },
                })
              }
              className="view-profile-link"
            >
              <img src={RedirectIcon} alt="redirect-icon" />
            </p>
          </div>
        </div>
        
        {/* Profile Summary Description */}
        <div className="profile-summary" style={{ marginTop: 16 }}>
          <Text strong style={{ fontSize: 16, color: "#071135", display: "block", marginBottom: 8 }}>
            Profile Summary Description
            {candidateData?.candidate?.discipline?.name && ` - ${candidateData?.candidate?.discipline?.name}`}
          </Text>
          <Text style={{ fontSize: 14, color: "#071135" }}>
            {candidateData?.candidate?.profileSummary}
          </Text>
        </div>
        
        {/* Personal Details Accordion */}
        <Collapse
          defaultActiveKey={["0"]}
          bordered={false}
          expandIconPosition="end"
          style={{ background: "transparent", marginTop: 16 }}
        >
          <Panel
            header={
              <Text strong style={{ fontSize: 16 }}>
                Personal Details
              </Text>
            }
            key="0"
          >
            <Row gutter={[16, 16]}>
              {/* Email */}
              <Col xs={24} sm={12} md={8}>
                <Space direction="vertical" size={4}>
                  <Space>
                    <MailOutlined />
                    <Text style={{ fontSize: 14, color: "#071135" }}>Email</Text>
                  </Space>
                  <Space>
                    <Text style={{ fontSize: 16, color: "#071135", fontWeight: 700 }}>
                      {candidateData?.candidate?.email || "----"}
                    </Text>
                    {candidateData?.candidate?.email && (
                      <CopyOutlined
                        style={{
                          cursor: "pointer",
                          color: "#1890ff",
                          fontSize: 14,
                        }}
                        onClick={() => handleCopy(candidateData?.candidate?.email)}
                      />
                    )}
                  </Space>
                </Space>
              </Col>

              {/* Phone Number */}
              <Col xs={24} sm={12} md={8}>
                <Space direction="vertical" size={4}>
                  <Space>
                    <PhoneOutlined />
                    <Text style={{ fontSize: 14, color: "#071135" }}>Phone Number</Text>
                  </Space>
                  <Space>
                    <Text style={{ fontSize: 16, color: "#071135", fontWeight: 700 }}>
                      {candidateData?.candidate?.countryCode ? `+${candidateData?.candidate?.countryCode} ` : ""}
                      {candidateData?.candidate?.phoneNumber || "----"}
                    </Text>
                    {candidateData?.candidate?.phoneNumber && (
                      <CopyOutlined
                        style={{
                          cursor: "pointer",
                          color: "#1890ff",
                          fontSize: 14,
                        }}
                        onClick={() => {
                          const phoneText = candidateData?.candidate?.countryCode 
                            ? `+${candidateData?.candidate?.countryCode} ${candidateData?.candidate?.phoneNumber}`
                            : candidateData?.candidate?.phoneNumber;
                          handleCopy(phoneText);
                        }}
                      />
                    )}
                  </Space>
                </Space>
              </Col>

              {/* D.O.B */}
              <Col xs={24} sm={12} md={8}>
                <Space direction="vertical" size={4}>
                  <Text style={{ fontSize: 14, color: "#071135" }}>D.O.B</Text>
                  {!isHidden ? (
                    <Text style={{ fontSize: 16, color: "#071135", fontWeight: 700 }}>
                      {candidateData?.candidate?.dob 
                        ? moment(candidateData?.candidate?.dob).format("DD MMM YYYY")
                        : "----"}
                    </Text>
                  ) : (
                    <Text style={{ fontSize: 16, color: "#071135", fontWeight: 700, filter: "blur(4px)" }}>
                      {candidateData?.candidate?.dob 
                        ? replaceString(moment(candidateData?.candidate?.dob).format("DD MMM YYYY"))
                        : "----"}
                    </Text>
                  )}
                </Space>
              </Col>

              {/* Gender */}
              <Col xs={24} sm={12} md={8}>
                <Space direction="vertical" size={4}>
                  <Text style={{ fontSize: 14, color: "#071135" }}>Gender</Text>
                  {!isHidden ? (
                    <Text style={{ fontSize: 16, color: "#071135", fontWeight: 700 }}>
                      {candidateData?.candidate?.gender
                        ? candidateData?.candidate?.gender?.charAt(0)?.toUpperCase() + candidateData?.candidate?.gender?.substring(1)
                        : "----"}
                    </Text>
                  ) : (
                    <Text style={{ fontSize: 16, color: "#071135", fontWeight: 700, filter: "blur(4px)" }}>
                      {replaceString(candidateData?.candidate?.gender) || "----"}
                    </Text>
                  )}
                </Space>
              </Col>

              {/* Ethnicity */}
              <Col xs={24} sm={12} md={8}>
                <Space direction="vertical" size={4}>
                  <Text style={{ fontSize: 14, color: "#071135" }}>Ethnicity</Text>
                  {!isHidden ? (
                    <Text style={{ fontSize: 16, color: "#071135", fontWeight: 700 }}>
                      {candidateData?.candidate?.ethnicity?.name || "----"}
                    </Text>
                  ) : (
                    <Text style={{ fontSize: 16, color: "#071135", fontWeight: 700, filter: "blur(4px)" }}>
                      {replaceString(candidateData?.candidate?.ethnicity?.name) || "----"}
                    </Text>
                  )}
                </Space>
              </Col>

              {/* Next of Kin */}
              {candidateData?.candidate?.nextOfKinRelation?.name && (
                <Col xs={24} sm={12} md={8}>
                  <Space direction="vertical" size={4}>
                    <Text style={{ fontSize: 14, color: "#071135" }}>Next of kin</Text>
                    {!isHidden ? (
                      <Text style={{ fontSize: 16, color: "#071135", fontWeight: 700 }}>
                        {candidateData?.candidate?.nextOfKinName}({candidateData?.candidate?.nextOfKinRelation?.name})
                        <br />
                        <br />
                        {candidateData?.candidate?.nextOfKinEmail}
                      </Text>
                    ) : (
                      <Text style={{ fontSize: 16, color: "#071135", fontWeight: 700, filter: "blur(4px)" }}>
                        {replaceString(`${candidateData?.candidate?.nextOfKinName} (${candidateData?.candidate?.nextOfKinRelation?.name})\n${candidateData?.candidate?.nextOfKinEmail}`)}
                      </Text>
                    )}
                  </Space>
                </Col>
              )}

              {/* Start / Notice */}
              <Col xs={24} sm={12} md={8}>
                <Space direction="vertical" size={4}>
                  <Text style={{ fontSize: 14, color: "#071135" }}>Start / Notice</Text>
                  <Text style={{ fontSize: 16, color: "#071135", fontWeight: 700 }}>
                    {candidateData?.candidate?.availabilityDetails?.name || "----"}
                  </Text>
                </Space>
              </Col>

              {/* Availability */}
              <Col xs={24} sm={12} md={8}>
                <Space direction="vertical" size={4}>
                  <Text style={{ fontSize: 14, color: "#071135" }}>Availability</Text>
                  <Text style={{ fontSize: 16, color: "#071135", fontWeight: 700 }}>
                    {candidateData?.candidate?.isAvailable 
                      ? "I'm available for work" 
                      : "I'm not available for work"}
                  </Text>
                </Space>
              </Col>
            </Row>
          </Panel>
        </Collapse>
        <div className="qualifications-section">
          {/* Experience Section */}

          <Collapse
            defaultActiveKey={["2"]}
            bordered={false}
            expandIconPosition="end"
            style={{ background: "transparent" }}
          >
            <Panel
              header={
                <Text strong style={{ fontSize: 16 }}>
                  Professional Experience
                </Text>
              }
              key="2"
            >
              {USE_GROUPED_JOB_EXPERIENCE
                ? groupExperiences(candidateData?.candidate?.experiences).map(
                    (group) => (
                      <JobPositionCardGrouped
                        key={group?.parent?._id || group?.parent?.id}
                        data={group.parent}
                        childExperiences={group.children}
                        isHidden={isHidden}
                      />
                    )
                  )
                : (candidateData?.candidate?.experiences || []).map(
                    (expData) => (
                      <JobPositionCard
                        key={expData?._id || expData?.id}
                        data={expData}
                        isHidden={isHidden}
                      />
                    )
                  )}
            </Panel>
          </Collapse>

          {/* Qualifications Section */}
          <Collapse
            defaultActiveKey={["1"]}
            bordered={false}
            expandIconPosition="end"
            style={{ background: "transparent" }}
          >
            <Panel
              header={
                <Text strong style={{ fontSize: 16 }}>
                  Qualifications
                </Text>
              }
              key="1"
            >
              {candidateData?.candidate?.qualifications?.map(
                (qualification, i) => (
                  <div
                    style={{
                      border: "1px solid  #E4E8ED",
                      borderRadius: 12,
                      padding: "12px",
                      display: "flex",
                      flexDirection: "column",
                      gap: 8,
                      marginBottom: 12,
                    }}
                    key={i}
                  >
                    <Row justify="space-between" align="middle">
                      <Col>
                        <Space>
                          <Text 
                            strong 
                            style={{ fontSize: 15, cursor: "pointer" }}
                            onClick={() => {
                              navigate(`/applicant-profile/${data?.profileId?.userId?._id}`, {
                                state: {
                                  id: advertId,
                                  ...(tabType ? { fromTracker: true } : {}),
                                  currentPage: currentPage,
                                  search: search !== null && search !== undefined ? search : undefined,
                                  tabType: tabType || undefined,
                                  filterType: filterType !== undefined ? filterType : undefined,
                                  campaignId: locationState?.campaignId,
                                  fromCampaignDetail: locationState?.fromCampaignDetail,
                                  originalSource: locationState?.originalSource
                                },
                              });
                            }}
                          >
                            {qualification?.qualificationType?.name}
                          </Text>
                          {qualification?.isValidate && (
                            <img src={CheckMark} alt="verified" style={{ height: 16 }} />
                          )}
                        </Space>
                      </Col>
                    </Row>

                    <Row gutter={16}>
                      <Col>
                        <Text>
                          Issue Date:{" "}
                          <strong>
                            {qualification?.issueDate
                              ? moment(qualification?.issueDate).format(
                                  "DD MMM YYYY"
                                )
                              : "----"}
                          </strong>
                        </Text>
                      </Col>
                      <Col>
                        <Text>
                          Expiry Date:{" "}
                          <strong>
                            {qualification?.expiryDate
                              ? moment(qualification?.expiryDate).format(
                                  "DD MMM YYYY"
                                )
                              : "----"}
                          </strong>
                        </Text>
                      </Col>
                    </Row>
                    <Row gutter={15} style={{ marginTop: "12px" }}>
                      <Col xs={8}>
                        <Text style={{ fontSize: 14, color: "#071135" }}>Member No./ID</Text>
                        {!isHidden ? (
                          <Text style={{ fontSize: 16, color: "#071135", fontWeight: 700, display: "block" }}>
                            {qualification?.memberId || "----"}
                          </Text>
                        ) : (
                          <Text style={{ fontSize: 16, color: "#071135", fontWeight: 700, display: "block", filter: "blur(4px)" }}>
                            {replaceString(qualification?.memberId) || "----"}
                          </Text>
                        )}
                      </Col>
                      <Col xs={8}>
                        <Text style={{ fontSize: 14, color: "#071135" }}>State/Country</Text>
                        <Text style={{ fontSize: 16, color: "#071135", fontWeight: 700, display: "block" }}>
                          {qualification?.stateOrCountry || "----"}
                        </Text>
                      </Col>
                      <Col xs={8}>
                        <Text style={{ fontSize: 14, color: "#071135" }}>
                          Licence Type/Class/Category
                        </Text>
                        {qualification?.licenceType?.length > 0 ? (
                          qualification.licenceType.map((item, index) => (
                            <Text key={index} style={{ fontSize: 16, color: "#071135", fontWeight: 700, display: "block" }}>
                              {item?.name}
                              {index !== qualification.licenceType.length - 1 ? ", " : ""}
                            </Text>
                          ))
                        ) : (
                          <Text style={{ fontSize: 16, color: "#071135", fontWeight: 700, display: "block" }}>
                            ----
                          </Text>
                        )}
                      </Col>
                    </Row>
                  </div>
                )
              )}
            </Panel>
          </Collapse>

          {/* Other Details Section */}
          <Collapse
            defaultActiveKey={["3"]}
            bordered={false}
            expandIconPosition="end"
            style={{ background: "transparent", marginTop: 16 }}
          >
            <Panel
              header={
                <Text strong style={{ fontSize: 16 }}>
                  Other Details
                </Text>
              }
              key="3"
            >
              <div style={{ border: "1px solid #E4E8ED", borderRadius: "12px", padding: "12px" }}>
              {/* Documents Section */}
                <div style={{ marginBottom: 24 }}>
                  <Text strong style={{ fontSize: 14, display: "block", marginBottom: 16 }}>
                    Documents
                  </Text>
                {candidateData?.candidate?.documents?.length > 0 ? [...candidateData.candidate.documents].sort((a, b) => {
                    const order = { "VISA": 0, "Proof of Age/Photo Card": 1, "Driver Licence": 2, "Passport": 3 };
                    const aOrder = order[a?.documentType?.name] !== undefined ? order[a?.documentType?.name] : 99;
                    const bOrder = order[b?.documentType?.name] !== undefined ? order[b?.documentType?.name] : 99;
                    return aOrder - bOrder;
                  }).map((doc, index) => (
                    <div key={index} style={{ marginBottom: 24, paddingBottom: 24, borderBottom: index < candidateData?.candidate?.documents?.length - 1 ? "1px solid #E4E8ED" : "none" }}>
                      <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
                        <Text strong style={{ fontSize: 15 }}>
                          {doc?.documentType?.name}
                        </Text>
                        {doc?.isVerfied && (
                          <img src={CheckMark} alt="verified" style={{ marginLeft: 8, height: 16 }} />
                        )}
                      </div>
                      <Row gutter={12}>
                        <Col span={12}>
                          <Text style={{ fontSize: 14, color: "#071135", display: "block", marginBottom: 4 }}>
                            {doc?.documentType?.name === "Passport"
                              ? "Nationality"
                              : doc?.documentType?.name === "Proof of Age/Photo Card"
                              ? "State"
                              : doc?.documentType?.name === "VISA"
                              ? "Visa Type Number"
                              : doc?.documentType?.name === "Driver Licence"
                              ? "State"
                              : "ID Number"}
                          </Text>
                          <Text style={{ fontSize: 16, color: "#071135", fontWeight: 700, display: "block" }}>
                            {doc?.documentType?.name === "Passport"
                              ? doc?.documentData?.nationality
                                  ?.substring(0, 1)
                                  ?.toUpperCase() +
                                doc?.documentData?.nationality?.substring(1) || "----"
                              : doc?.documentType?.name === "Proof of Age/Photo Card"
                              ? (doc?.documentData?.state && typeof doc?.documentData?.state === 'object' && doc?.documentData?.state?.name)
                                ? doc?.documentData?.state?.name
                                : (doc?.documentData?.state && typeof doc?.documentData?.state === 'string')
                                ? doc?.documentData?.state
                                : "----"
                              : doc?.documentType?.name === "VISA"
                              ? doc?.documentData?.visaTypeNo?.name || "----"
                              : doc?.documentType?.name === "Driver Licence"
                              ? doc?.documentData?.state || "-"
                              : doc?.documentData?.idNumber || "----"}
                          </Text>
                        </Col>
                        <Col span={12}>
                          <Text style={{ fontSize: 14, color: "#071135", display: "block", marginBottom: 4 }}>
                            {doc?.documentType?.name === "Passport"
                              ? "Passport Number"
                              : doc?.documentType?.name === "Driver Licence"
                              ? "Licence Number"
                              : doc?.documentType?.name === "VISA"
                              ? "Grant Number"
                              : "ID Number"}
                          </Text>
                          {doc?.documentType?.name === "Passport" ? (
                            !isHidden ? (
                              <Text style={{ fontSize: 16, color: "#071135", fontWeight: 700, display: "block" }}>
                                {doc?.documentData?.passportNumber || "----"}
                              </Text>
                            ) : (
                              <Text style={{ fontSize: 16, color: "#071135", fontWeight: 700, display: "block", filter: "blur(4px)" }}>
                                {replaceString(doc?.documentData?.passportNumber) || "----"}
                              </Text>
                            )
                          ) : doc?.documentType?.name === "Driver Licence" ? (
                            !isHidden ? (
                              <Text style={{ fontSize: 16, color: "#071135", fontWeight: 700, display: "block" }}>
                                {doc?.documentData?.licenceNumber || doc?.documentData?.licenseNumber || "----"}
                              </Text>
                            ) : (
                              <Text style={{ fontSize: 16, color: "#071135", fontWeight: 700, display: "block", filter: "blur(4px)" }}>
                                {replaceString(doc?.documentData?.licenceNumber || doc?.documentData?.licenseNumber) || "----"}
                              </Text>
                            )
                          ) : doc?.documentType?.name === "VISA" ? (
                            !isHidden ? (
                              <Text style={{ fontSize: 16, color: "#071135", fontWeight: 700, display: "block" }}>
                                {doc?.documentData?.grantNo || "----"}
                              </Text>
                            ) : (
                              <Text style={{ fontSize: 16, color: "#071135", fontWeight: 700, display: "block", filter: "blur(4px)" }}>
                                {replaceString(doc?.documentData?.grantNo) || "----"}
                              </Text>
                            )
                          ) : doc?.documentType?.name === "Proof of Age/Photo Card" ? (
                            !isHidden ? (
                              <Text style={{ fontSize: 16, color: "#071135", fontWeight: 700, display: "block" }}>
                                {doc?.documentData?.idNumber || "----"}
                              </Text>
                            ) : (
                              <Text style={{ fontSize: 16, color: "#071135", fontWeight: 700, display: "block", filter: "blur(4px)" }}>
                                {replaceString(doc?.documentData?.idNumber) || "----"}
                              </Text>
                            )
                          ) : (
                            <Text style={{ fontSize: 16, color: "#071135", fontWeight: 700, display: "block" }}>
                              {doc?.documentData?.idNumber || "----"}
                            </Text>
                          )}
                        </Col>
                      </Row>
                      <Row gutter={12} style={{ marginTop: 12 }}>
                        {doc?.documentType?.name === "Driver Licence" && (
                          <>
                            <Col span={12}>
                              <Text style={{ fontSize: 14, color: "#071135", display: "block", marginBottom: 4 }}>
                                Class
                              </Text>
                              <Text style={{ fontSize: 16, color: "#071135", fontWeight: 700, display: "block" }}>
                                {Array.isArray(doc?.documentData?.class) && doc.documentData.class.length > 0
                                  ? doc.documentData.class.join(", ")
                                  : "----"}
                              </Text>
                            </Col>
                            <Col span={12}>
                              <Text style={{ fontSize: 14, color: "#071135", display: "block", marginBottom: 4 }}>
                                Conditions
                              </Text>
                              <Text style={{ fontSize: 16, color: "#071135", fontWeight: 700, display: "block" }}>
                                {Array.isArray(doc?.documentData?.conditions) && doc.documentData.conditions.length > 0
                                  ? doc.documentData.conditions.join(", ")
                                  : "----"}
                              </Text>
                            </Col>
                          </>
                        )}
                        <Col span={12}>
                          <Text style={{ fontSize: 14, color: "#071135", display: "block", marginBottom: 4 }}>
                            {doc?.documentType?.name === "VISA" ? "Issue Date" : "Expiry Date"}
                          </Text>
                          {doc?.documentType?.name === "VISA" || doc?.documentType?.name === "Proof of Age/Photo Card" || doc?.documentType?.name === "Passport" || doc?.documentType?.name === "Driver Licence" ? (
                            <Text style={{ fontSize: 16, color: "#071135", fontWeight: 700, display: "block" }}>
                              {doc?.documentType?.name === "VISA"
                                ? (doc?.documentData?.issueDate
                                    ? moment(doc?.documentData?.issueDate).format("DD MMM YYYY")
                                    : "----")
                                : (doc?.documentData?.expiryDate
                                    ? moment(doc?.documentData?.expiryDate).format("DD MMM YYYY")
                                    : "----")}
                            </Text>
                          ) : !isHidden ? (
                            <Text style={{ fontSize: 16, color: "#071135", fontWeight: 700, display: "block" }}>
                              {doc?.documentData?.expiryDate
                                ? moment(doc?.documentData?.expiryDate).format("DD MMM YYYY")
                                : "----"}
                            </Text>
                          ) : (
                            <Text style={{ fontSize: 16, color: "#071135", fontWeight: 700, display: "block", filter: "blur(4px)" }}>
                              {doc?.documentData?.expiryDate
                                ? replaceString(moment(doc?.documentData?.expiryDate).format("DD MMM YYYY"))
                                : "----"}
                            </Text>
                          )}
                        </Col>
                        {doc?.documentType?.name === "VISA" && (
                          <Col span={12}>
                            <Text style={{ fontSize: 14, color: "#071135", display: "block", marginBottom: 4 }}>
                              Expiry Date
                            </Text>
                            <Text style={{ fontSize: 16, color: "#071135", fontWeight: 700, display: "block" }}>
                              {doc?.documentData?.expiryDate
                                ? moment(doc?.documentData?.expiryDate).format("DD MMM YYYY")
                                : "----"}
                            </Text>
                          </Col>
                        )}
                      </Row>
                    </div>
                  )) : (
                    <Text style={{ fontSize: 14, color: "#6B7280" }}>No documents uploaded</Text>
              )}
                </div>

              {/* Additional Personal Details Section */}
                <div>
                  <Text strong style={{ fontSize: 14, display: "block", marginBottom: 16 }}>
                    Additional Personal Details
                  </Text>
                  <Row gutter={16}>
                        <Col xs={24} sm={12} md={8}>
                          <Space direction="vertical" size={4}>
                            <Text style={{ fontSize: 14, color: "#071135" }}>Tax File Number (TFN)</Text>
                            {!isHidden ? (
                              <Text style={{ fontSize: 16, color: "#071135", fontWeight: 700 }}>
                          {candidateData?.candidate?.otherDetails?.[0]?.tfnNo || "----"}
                              </Text>
                            ) : (
                              <Text style={{ fontSize: 16, color: "#071135", fontWeight: 700, filter: "blur(4px)" }}>
                          {replaceString(candidateData?.candidate?.otherDetails?.[0]?.tfnNo) || "----"}
                              </Text>
                            )}
                          </Space>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                          <Space direction="vertical" size={4}>
                            <Text style={{ fontSize: 14, color: "#071135" }}>USI No.</Text>
                            {!isHidden ? (
                              <Text style={{ fontSize: 16, color: "#071135", fontWeight: 700 }}>
                          {candidateData?.candidate?.otherDetails?.[0]?.usiNo || "----"}
                              </Text>
                            ) : (
                              <Text style={{ fontSize: 16, color: "#071135", fontWeight: 700, filter: "blur(4px)" }}>
                          {replaceString(candidateData?.candidate?.otherDetails?.[0]?.usiNo) || "----"}
                              </Text>
                            )}
                          </Space>
                        </Col>
                    {/* <Col xs={24} sm={12} md={8}>
                      <Space direction="vertical" size={4}>
                        <Text style={{ fontSize: 14, color: "#071135" }}>Citizenship</Text>
                        {candidateData?.candidate?.candidateVisa?.length > 0 
                          ? (!candidateData?.candidate?.candidateVisa[0]?.isAustralian 
                              ? (
                                <Tag 
                                  color="orange" 
                                  style={{ 
                                    fontSize: "14px", 
                                    padding: "4px 12px",
                                    borderRadius: "6px",
                                    fontWeight: 700,
                                    margin: 0
                                  }}
                                >
                                  Non-Australian
                                </Tag>
                              )
                              : (
                                <Tag 
                                  color="green" 
                                  style={{ 
                                    fontSize: "14px", 
                                    padding: "4px 12px",
                                    borderRadius: "6px",
                                    fontWeight: 700,
                                    margin: 0
                                  }}
                                >
                                  Australian/New Zealand
                                </Tag>
                              ))
                          : (
                            <Tag 
                              color="green" 
                              style={{ 
                                fontSize: "14px", 
                                padding: "4px 12px",
                                borderRadius: "6px",
                                fontWeight: 700,
                                margin: 0
                              }}
                            >
                              Australian/New Zealand
                            </Tag>
                          )}
                      </Space>
                    </Col> */}
                  </Row>
                      <Divider style={{ margin: "16px 0" }} />
                      <Text strong style={{ fontSize: 14, display: "block", marginBottom: 16 }}>
                        Superannuation
                      </Text>
                      <Row gutter={16}>
                        <Col xs={24} sm={12} md={8}>
                          <Space direction="vertical" size={4}>
                            <Text style={{ fontSize: 14, color: "#071135" }}>Fund Name</Text>
                            {!isHidden ? (
                              <Text style={{ fontSize: 16, color: "#071135", fontWeight: 700 }}>
                          {candidateData?.candidate?.otherDetails?.[0]?.superannuationDetails?.fundName?.name || "----"}
                              </Text>
                            ) : (
                              <Text style={{ fontSize: 16, color: "#071135", fontWeight: 700, filter: "blur(4px)" }}>
                          {replaceString(candidateData?.candidate?.otherDetails?.[0]?.superannuationDetails?.fundName?.name) || "----"}
                              </Text>
                            )}
                          </Space>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                          <Space direction="vertical" size={4}>
                            <Text style={{ fontSize: 14, color: "#071135" }}>Fund USI/ABN</Text>
                            {!isHidden ? (
                              <Text style={{ fontSize: 16, color: "#071135", fontWeight: 700 }}>
                          {candidateData?.candidate?.otherDetails?.[0]?.superannuationDetails?.fundUsiAbn?.name || 
                           candidateData?.candidate?.otherDetails?.[0]?.superannuationDetails?.fundUsiAbn || 
                                 "----"}
                              </Text>
                            ) : (
                              <Text style={{ fontSize: 16, color: "#071135", fontWeight: 700, filter: "blur(4px)" }}>
                                {replaceString(
                            candidateData?.candidate?.otherDetails?.[0]?.superannuationDetails?.fundUsiAbn?.name || 
                            candidateData?.candidate?.otherDetails?.[0]?.superannuationDetails?.fundUsiAbn
                                ) || "----"}
                              </Text>
                            )}
                          </Space>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                          <Space direction="vertical" size={4}>
                            <Text style={{ fontSize: 14, color: "#071135" }}>Member Number</Text>
                            {!isHidden ? (
                              <Text style={{ fontSize: 16, color: "#071135", fontWeight: 700 }}>
                          {candidateData?.candidate?.otherDetails?.[0]?.superannuationDetails?.memberNumber || "----"}
                              </Text>
                            ) : (
                              <Text style={{ fontSize: 16, color: "#071135", fontWeight: 700, filter: "blur(4px)" }}>
                          {replaceString(candidateData?.candidate?.otherDetails?.[0]?.superannuationDetails?.memberNumber) || "----"}
                              </Text>
                            )}
                          </Space>
                        </Col>
                      </Row>
                      <Divider style={{ margin: "16px 0" }} />
                      <Text strong style={{ fontSize: 14, display: "block", marginBottom: 16 }}>
                        Long Service Leave
                      </Text>
                      <Row gutter={16}>
                    {candidateData?.candidate?.otherDetails?.[0]?.lslDetails?.length > 0 ? (
                      candidateData?.candidate?.otherDetails[0]?.lslDetails.map((item, index) => (
                          <React.Fragment key={index}>
                            <Col xs={12} md={8} style={{ marginTop: index !== 0 ? "12px" : "0px" }}>
                              <Space direction="vertical" size={4}>
                                <Text style={{ fontSize: 14, color: "#071135" }}>State</Text>
                                {!isHidden ? (
                                  <Text style={{ fontSize: 16, color: "#071135", fontWeight: 700 }}>
                                    {item?.state?.name || "----"}
                                  </Text>
                                ) : (
                                  <Text style={{ fontSize: 16, color: "#071135", fontWeight: 700, filter: "blur(4px)" }}>
                                    {replaceString(item?.state?.name) || "----"}
                                  </Text>
                                )}
                              </Space>
                            </Col>
                            <Col xs={12} md={16} style={{ marginTop: index !== 0 ? "12px" : "0px" }}>
                              <Space direction="vertical" size={4}>
                                <Text style={{ fontSize: 14, color: "#071135" }}>Member Number</Text>
                                {!isHidden ? (
                                  <Text style={{ fontSize: 16, color: "#071135", fontWeight: 700 }}>
                                    {item?.memberNumber || "----"}
                                  </Text>
                                ) : (
                                  <Text style={{ fontSize: 16, color: "#071135", fontWeight: 700, filter: "blur(4px)" }}>
                                    {replaceString(item?.memberNumber) || "----"}
                                  </Text>
                                )}
                              </Space>
                            </Col>
                          </React.Fragment>
                      ))
                    ) : (
                      <>
                        <Col xs={12} md={8}>
                          <Space direction="vertical" size={4}>
                            <Text style={{ fontSize: 14, color: "#071135" }}>State</Text>
                            <Text style={{ fontSize: 16, color: "#071135", fontWeight: 700 }}>----</Text>
                          </Space>
                        </Col>
                        <Col xs={12} md={16}>
                          <Space direction="vertical" size={4}>
                            <Text style={{ fontSize: 14, color: "#071135" }}>Member Number</Text>
                            <Text style={{ fontSize: 16, color: "#071135", fontWeight: 700 }}>----</Text>
                          </Space>
                        </Col>
                    </>
                  )}
                  </Row>
                      <Divider style={{ margin: "16px 0" }} />
                      <Text strong style={{ fontSize: 14, display: "block", marginBottom: 16 }}>
                        PPE Details
                      </Text>
                      <Row gutter={16}>
                        <Col xs={8} md={8}>
                          <Space direction="vertical" size={4}>
                            <Text style={{ fontSize: 14, color: "#071135" }}>Shirt</Text>
                            <Text style={{ fontSize: 16, color: "#071135", fontWeight: 700 }}>
                          {candidateData?.candidate?.otherDetails?.[0]?.ppeDetails?.shirt?.name || "----"}
                            </Text>
                          </Space>
                        </Col>
                        <Col xs={8} md={8}>
                          <Space direction="vertical" size={4}>
                            <Text style={{ fontSize: 14, color: "#071135" }}>Pants</Text>
                            <Text style={{ fontSize: 16, color: "#071135", fontWeight: 700 }}>
                          {candidateData?.candidate?.otherDetails?.[0]?.ppeDetails?.pants?.name || "----"}
                            </Text>
                          </Space>
                        </Col>
                        <Col xs={8} md={8}>
                          <Space direction="vertical" size={4}>
                            <Text style={{ fontSize: 14, color: "#071135" }}>Boots</Text>
                            <Text style={{ fontSize: 16, color: "#071135", fontWeight: 700 }}>
                          {candidateData?.candidate?.otherDetails?.[0]?.ppeDetails?.boots?.name || "----"}
                            </Text>
                          </Space>
                        </Col>
                      </Row>
                      <Divider style={{ margin: "16px 0" }} />
                      <Text strong style={{ fontSize: 14, display: "block", marginBottom: 16 }}>
                        Travel Details
                      </Text>
                      <Row gutter={16}>
                        <Col xs={12} md={8}>
                          <Space direction="vertical" size={4}>
                            <Text style={{ fontSize: 14, color: "#071135" }}>Preferred Airline</Text>
                            <Text style={{ fontSize: 16, color: "#071135", fontWeight: 700 }}>
                          {candidateData?.candidate?.otherDetails?.[0]?.travelDetails?.preferredAirline || "----"}
                            </Text>
                          </Space>
                        </Col>
                        <Col xs={12} md={8}>
                          <Space direction="vertical" size={4}>
                            <Text style={{ fontSize: 14, color: "#071135" }}>Frequent Flyer Number</Text>
                            <Text style={{ fontSize: 16, color: "#071135", fontWeight: 700 }}>
                          {candidateData?.candidate?.otherDetails?.[0]?.travelDetails?.frequentFlyerNumber || "----"}
                            </Text>
                          </Space>
                        </Col>
                      </Row>
                      {(candidateData?.candidate?.otherDetails?.[0]?.policeClearanceDetails?.certifyingAuthority?.name || candidateData?.candidate?.otherDetails?.[0]?.policeClearanceDetails?.issueDate) && (
                        <>
                          <Divider style={{ margin: "16px 0" }} />
                          <Text strong style={{ fontSize: 14, display: "block", marginBottom: 16 }}>
                            Police Clearance
                            {candidateData?.candidate?.otherDetails?.[0]?.policeClearanceDetails?.isVerified && (
                              <img src={CheckMark} alt="verified" style={{ marginLeft: 8, height: 16 }} />
                            )}
                          </Text>
                          <Row gutter={16}>
                            <Col xs={24} md={12}>
                              <Space direction="vertical" size={4}>
                                <Text style={{ fontSize: 14, color: "#071135" }}>Certifying Authority</Text>
                                <Text style={{ fontSize: 16, color: "#071135", fontWeight: 700 }}>
                                  {candidateData?.candidate?.otherDetails?.[0]?.policeClearanceDetails?.certifyingAuthority?.name || "----"}
                                </Text>
                              </Space>
                            </Col>
                            <Col xs={24} md={12}>
                              <Space direction="vertical" size={4}>
                                <Text style={{ fontSize: 14, color: "#071135" }}>Issue Date</Text>
                                <Text style={{ fontSize: 16, color: "#071135", fontWeight: 700 }}>
                                  {candidateData?.candidate?.otherDetails?.[0]?.policeClearanceDetails?.issueDate
                                    ? moment(candidateData?.candidate?.otherDetails[0]?.policeClearanceDetails?.issueDate).format("DD MMM YYYY")
                                    : "----"}
                                </Text>
                              </Space>
                            </Col>
                          </Row>
                        </>
                      )}
                      {candidateData?.candidate?.medicalAssessment?.length > 0 &&
                       candidateData?.candidate?.medicalAssessment[0]?.medicals?.length > 0 && (
                        <>
                          <Divider style={{ margin: "16px 0" }} />
                          <Text strong style={{ fontSize: 14, display: "block", marginBottom: 16 }}>
                            Medical Assessment
                          </Text>
                          {candidateData?.candidate?.medicalAssessment[0]?.medicals?.map((med, index) => (
                            <Row key={index} gutter={16} style={{ marginBottom: index < candidateData?.candidate?.medicalAssessment[0]?.medicals?.length - 1 ? 16 : 0 }}>
                              <Col xs={24} md={12}>
                                <Space direction="vertical" size={4}>
                                  <Text style={{ fontSize: 14, color: "#071135" }}>Medical Assessment Type</Text>
                                  <Text style={{ fontSize: 16, color: "#071135", fontWeight: 700 }}>
                                    {med?.medicalAssessment?.name || "----"}
                                  </Text>
                                </Space>
                              </Col>
                              <Col xs={24} md={12}>
                                <Space direction="vertical" size={4}>
                                  <Text style={{ fontSize: 14, color: "#071135" }}>Issue Date</Text>
                                  <Text style={{ fontSize: 16, color: "#071135", fontWeight: 700 }}>
                                    {med?.issueDate
                                      ? moment(med?.issueDate).format("DD MMM YYYY")
                                      : "----"}
                                  </Text>
                                </Space>
                              </Col>
                            </Row>
                          ))}
                        </>
                      )}
                </div>
              </div>
            </Panel>
          </Collapse>
        </div>
      </Spin>
      <UpdatestatusPopup
        updatePopup={updatePopup}
        setUpdatePopup={setUpdatePopup}
        setUpdatePopUp={setUpdatePopup}
        candidateSelectedData={candidateSelectedData}
        onSubmit={updateCandidateJobStatus}
        name={candidateData?.candidate?.name}
      />
    </Drawer>
  );
};

export default ProfileDrawer;
